import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

// jspdf-autotable v5 only self-registers when jsPDF is available as a browser
// global (window.jsPDF / window.jspdf), which is the script-tag scenario. Under a
// bundler that global does not exist, so the bare `import "jspdf-autotable"`
// side effect no longer patches jsPDF the way v3 did. Applying the plugin
// explicitly restores `doc.autoTable(...)` and the `doc.lastAutoTable` sugar.
applyPlugin(jsPDF);

/**
 * Validates parsed report data for PDF generation eligibility.
 *
 * @param {Object} reportData - Parsed CSV data
 * @returns {{ valid: boolean, error?: string }} - Validation result
 */
export const validateReportData = (reportData) => {
  if (!reportData || typeof reportData !== "object") {
    return { valid: false, error: "Report data is missing" };
  }

  if (!reportData.metadata || typeof reportData.metadata !== "object") {
    return { valid: false, error: "Report metadata is missing or invalid" };
  }

  // Allow reports with metadata + headers but no data rows (empty report)
  if (
    !reportData.tableData ||
    !Array.isArray(reportData.tableData.headers)
  ) {
    return { valid: false, error: "Report table data is missing" };
  }

  return { valid: true };
};

/**
 * Generates the download filename for a report PDF.
 *
 * @param {Object} report - Report object from the API
 * @param {string} report.name - Report name
 * @param {string} report.report_type - Report type identifier
 * @param {string} report.created_at - ISO timestamp of report creation
 * @returns {string} - Filename in pattern: {name}_{type}_{YYYY-MM-DD}.pdf
 */
export const generatePDFFilename = (report) => {
  const name = (report.name || "").replace(/\s+/g, "_");
  const type = report.report_type || "";
  let date = "";
  if (report.created_at) {
    // Handle both string and other types (Date, timestamp)
    const createdAt = typeof report.created_at === 'string'
      ? report.created_at
      : new Date(report.created_at).toISOString();
    date = createdAt.substring(0, 10);
  }

  return `${name}_${type}_${date}.pdf`;
};

/**
 * Generates a PDF document from parsed report data.
 *
 * @param {Object} reportData - Parsed CSV data from fetchAndParseCSV
 * @param {Object} reportData.metadata - Key-value pairs of report metadata
 * @param {Object} reportData.tableData - Table data with headers and rows
 * @param {string[]} reportData.tableData.headers - Column header names
 * @param {string[][]} reportData.tableData.rows - 2D array of row data
 * @returns {Blob} - PDF file as a Blob
 */
export const generateReportPDF = (reportData) => {
  // Reports whose CSV contains several titled sections (e.g. Vehicle Performance
  // with "Best Of 5 Vehicle" / "Worst of 5 Vehicle") render one table per section.
  const sections = Array.isArray(reportData.sections) ? reportData.sections : [];
  const titledSections = sections.filter((section) => section.headers?.length > 0);
  if (titledSections.length > 1) {
    return generateSectionedReportPDF(reportData.metadata, titledSections);
  }

  const { headers, rows } = reportData.tableData;
  const columnCount = headers.length;

  // Choose orientation - always landscape for better table readability
  const orientation = "landscape";

  const doc = new jsPDF({
    orientation,
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 30;
  let yPosition = margin;

  // --- Report Title ---
  const reportTitle = reportData.metadata["Report Name"] || "Report";
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text(reportTitle, margin, yPosition);
  yPosition += 8;

  // Separator line under title
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 14;

  // --- Metadata Section (2 columns, compact) ---
  const metadataEntries = Object.entries(reportData.metadata).filter(
    ([key]) => key !== "Report Name"
  );

  if (metadataEntries.length > 0) {
    doc.setFontSize(8);
    const colWidth = (pageWidth - margin * 2) / 2;
    const lineHeight = 13;

    metadataEntries.forEach(([key, value], index) => {
      const col = index % 2;
      const xPos = margin + col * colWidth;

      doc.setFont(undefined, "bold");
      doc.setTextColor(80, 80, 80);
      doc.text(`${key}:`, xPos, yPosition);

      const keyWidth = doc.getTextWidth(`${key}: `);
      doc.setFont(undefined, "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(String(value), xPos + keyWidth, yPosition);

      if (col === 1 || index === metadataEntries.length - 1) {
        yPosition += lineHeight;
      }
    });

    yPosition += 8;

    // Separator line after metadata
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 12;
  }

  // --- Adaptive Table Sizing ---
  // Calculate optimal font size based on column count
  let tableFontSize;
  let headerFontSize;
  let cellPadding;

  if (columnCount <= 6) {
    tableFontSize = 8;
    headerFontSize = 8;
    cellPadding = 4;
  } else if (columnCount <= 10) {
    tableFontSize = 7;
    headerFontSize = 7.5;
    cellPadding = 3;
  } else if (columnCount <= 15) {
    tableFontSize = 6;
    headerFontSize = 6.5;
    cellPadding = 2.5;
  } else {
    tableFontSize = 5;
    headerFontSize = 5.5;
    cellPadding = 2;
  }

  // --- Column width optimization ---
  // Detect columns that need minimum widths (dates, IDs) vs columns with very long content
  const columnStyles = {};
  const availableWidth = pageWidth - margin * 2;

  if (headers.length > 0 && rows.length > 0) {
    // Analyze content to determine column characteristics
    headers.forEach((header, colIdx) => {
      const headerLower = header.toLowerCase();
      const isDateColumn = headerLower.includes("date") || headerLower.includes("time") ||
        headerLower.includes("period") || headerLower.includes("created") ||
        headerLower.includes("start") || headerLower.includes("end");
      const isIdColumn = headerLower.includes("imei") || headerLower === "id" ||
        headerLower.includes("device id") || headerLower.includes("packet");

      // Check max content length in this column
      let maxContentLength = header.length;
      const sampleSize = Math.min(rows.length, 10);
      for (let i = 0; i < sampleSize; i++) {
        const cellLen = (rows[i][colIdx] || "").length;
        if (cellLen > maxContentLength) maxContentLength = cellLen;
      }

      if (isDateColumn) {
        // Date/time columns need minimum width to be readable
        columnStyles[colIdx] = { cellWidth: Math.max(75, Math.min(120, availableWidth / columnCount * 1.5)), overflow: "linebreak" };
      } else if (isIdColumn) {
        columnStyles[colIdx] = { cellWidth: Math.max(60, availableWidth / columnCount), overflow: "linebreak" };
      } else if (maxContentLength > 100) {
        // Very long content (base64, hashes) - limit max width and allow wrapping
        columnStyles[colIdx] = { cellWidth: Math.min(200, availableWidth * 0.4), overflow: "linebreak" };
      }
    });
  }

  // --- Data Table (only render if headers exist) ---
  if (headers.length > 0) {
    doc.autoTable({
      startY: yPosition,
      head: [headers],
      body: rows.length > 0 ? rows : [headers.map(() => "—")],
      margin: { left: margin, right: margin, top: margin, bottom: margin },
      styles: {
        fontSize: tableFontSize,
        cellPadding,
        overflow: "linebreak",
        lineColor: [220, 220, 220],
        lineWidth: 0.5,
      },
      headStyles: {
        fontSize: headerFontSize,
        fillColor: [7, 103, 208],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        cellPadding: cellPadding + 1,
      },
      bodyStyles: {
        valign: "middle",
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      columnStyles,
      tableWidth: "wrap",
      theme: "grid",
      didDrawPage: (data) => {
      // Footer with page numbers
      const pageCount = doc.internal.getNumberOfPages();
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${currentPage} of ${pageCount}`,
        pageWidth - margin,
        pageHeight - 15,
        { align: "right" }
      );
      // Report name on each page footer
      doc.text(reportTitle, margin, pageHeight - 15);
    },
  });
  } else if (Object.keys(reportData.metadata).length > 0) {
    // No table headers - just show "No data available" message
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text("No data available for this report.", margin, yPosition);
  }

  return doc.output("blob");
};

/**
 * Generates a PDF document for reports whose CSV holds several titled sections,
 * rendering one table per section (e.g. "Best Of 5 Vehicle" / "Worst of 5 Vehicle").
 *
 * @param {Object} metadata - Key-value pairs of report metadata
 * @param {Array} sections - Array of { title, headers, rows }
 * @returns {Blob} - PDF file as a Blob
 */
export const generateSectionedReportPDF = (metadata, sections) => {
  const orientation = "landscape";
  const doc = new jsPDF({
    orientation,
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 30;
  let yPosition = margin;

  // --- Report Title ---
  const reportTitle = metadata["Report Name"] || "Fuel Analysis Report";
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text(reportTitle, margin, yPosition);
  yPosition += 8;

  // Separator line under title
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 14;

  // --- Metadata Section (2 columns, compact) ---
  const metadataEntries = Object.entries(metadata).filter(
    ([key]) => key !== "Report Name"
  );

  if (metadataEntries.length > 0) {
    doc.setFontSize(8);
    const colWidth = (pageWidth - margin * 2) / 2;
    const lineHeight = 13;

    metadataEntries.forEach(([key, value], index) => {
      const col = index % 2;
      const xPos = margin + col * colWidth;

      doc.setFont(undefined, "bold");
      doc.setTextColor(80, 80, 80);
      doc.text(`${key}:`, xPos, yPosition);

      const keyWidth = doc.getTextWidth(`${key}: `);
      doc.setFont(undefined, "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(String(value), xPos + keyWidth, yPosition);

      if (col === 1 || index === metadataEntries.length - 1) {
        yPosition += lineHeight;
      }
    });

    yPosition += 8;

    // Separator line after metadata
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 12;
  }

  // --- Render each section as a separate table ---
  const tableFontSize = 6;
  const headerFontSize = 6.5;
  const cellPadding = 2.5;

  sections.forEach((section) => {
    const { title, headers, rows } = section;

    // Keep a section title with its table: start a new page when space runs out
    if (yPosition > pageHeight - 100) {
      doc.addPage();
      yPosition = margin;
    }

    // Section title (omitted for unnamed sections)
    if (title) {
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(title, margin, yPosition);
      yPosition += 14;
    }

    if (headers.length > 0) {
      doc.autoTable({
        startY: yPosition,
        head: [headers],
        body: rows.length > 0 ? rows : [headers.map(() => "—")],
        margin: { left: margin, right: margin, top: margin, bottom: margin },
        styles: {
          fontSize: tableFontSize,
          cellPadding,
          overflow: "linebreak",
          lineColor: [220, 220, 220],
          lineWidth: 0.5,
        },
        headStyles: {
          fontSize: headerFontSize,
          fillColor: [7, 103, 208],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
          cellPadding: cellPadding + 1,
        },
        bodyStyles: {
          valign: "middle",
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        tableWidth: "wrap",
        theme: "grid",
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Page ${currentPage} of ${pageCount}`,
            pageWidth - margin,
            pageHeight - 15,
            { align: "right" }
          );
          doc.text(reportTitle, margin, pageHeight - 15);
        },
      });

      // Update yPosition after table
      yPosition = doc.lastAutoTable.finalY + 20;
    }
  });

  return doc.output("blob");
};
